import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, MapPin, Store, ShieldCheck, Gem, Lock, MessageCircle, Instagram, ArrowLeft, RotateCcw } from 'lucide-react';

const WHATSAPP_LINK = "https://wa.me/5500000000000";
const INSTAGRAM_LINK = "https://www.instagram.com/outletpremiumltdaa?igsh=MWxodmJkanRhc2Y1eg%3D%3D";

// FAQ item com expand/collapse
const FaqItem = ({ icon: Icon, question, answer, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-neutral-200 last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-4 py-5 px-1 text-left group"
      >
        <div className="w-10 h-10 rounded-full bg-[#2e2923]/10 flex items-center justify-center flex-shrink-0">
          <Icon className="h-5 w-5 text-[#C4A35A]" />
        </div>
        <span className="flex-1 text-sm md:text-base font-semibold text-[#1a1a1a] group-hover:text-[#C4A35A] transition-colors">
          {question}
        </span>
        <ChevronDown className={`h-5 w-5 text-neutral-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 opacity-100 pb-5' : 'max-h-0 opacity-0'}`}>
        <div className="pl-[56px] pr-4 text-sm text-neutral-600 leading-relaxed space-y-3">
          {answer}
        </div>
      </div>
    </div>
  );
};

// Seção expandível para Privacidade e Termos
const ExpandableSection = ({ id, title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div id={id} className="border-b border-neutral-200 last:border-b-0 scroll-mt-24">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-5 px-5 md:px-6 text-left hover:bg-neutral-50 transition-colors"
      >
        <span className="text-sm md:text-base font-bold text-[#1a1a1a] uppercase tracking-wider">{title}</span>
        <ChevronDown className={`h-5 w-5 text-neutral-400 transition-transform duration-300 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-400 ${isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="px-5 md:px-6 pb-6">
          {children}
        </div>
      </div>
    </div>
  );
};

export default function AboutPage() {
  const location = useLocation();

  // Scroll to anchor on load
  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-[#1a1614] py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h1
            className="text-3xl md:text-5xl font-semibold text-[#F5F5F0] mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}
          >
            Outlet Premium
          </h1>
          <div className="w-12 h-[2px] bg-[#C4A35A] mx-auto mb-6" />
          <p className="text-neutral-400 text-sm md:text-base leading-relaxed max-w-xl mx-auto mb-6">
            Moda de qualidade direto da fábrica para você, com preços acessíveis a todo o público.
          </p>
          <a
            href={INSTAGRAM_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#833AB4] via-[#C13584] to-[#F77737] text-white rounded-full text-xs font-semibold hover:opacity-90 transition-opacity"
          >
            <Instagram className="h-4 w-4" />
            @outletpremiumltdaa
          </a>
        </div>
      </section>

      {/* Quem Somos - Expandível */}
      <section className="max-w-3xl mx-auto px-4 py-12 md:py-16">
        <div className="border border-neutral-200 rounded-xl overflow-hidden">
          <ExpandableSection id="quem-somos" title="Quem Somos" defaultOpen={true}>
            <div className="text-sm md:text-base text-neutral-600 leading-relaxed space-y-4">
              <p>
                Fabricamos vários estilos de roupas desde o tecido até a fase final. Fornecemos para muitas lojas com nome consolidado no mercado e há algum tempo decidimos vender para o público do varejo!
              </p>
              <p>
                Aqui você encontrará roupas com costuras e tecidos de alta qualidade com valores acessíveis a todo o público.
              </p>
              <p className="text-[#1a1a1a] font-medium italic border-l-2 border-[#C4A35A] pl-4">
                Somos conhecidos por nossa qualidade e o valor acessível ao cliente.
              </p>
            </div>
          </ExpandableSection>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-3xl mx-auto px-4">
        <div className="border-t border-neutral-200" />
      </div>

      {/* Perguntas Frequentes */}
      <section className="max-w-3xl mx-auto px-4 py-12 md:py-16">
        <h2 className="text-lg md:text-xl font-bold text-[#1a1a1a] mb-8 uppercase tracking-wider" id="trocas">
          Dúvidas Frequentes
        </h2>

        <div className="bg-white rounded-xl border border-neutral-200 px-4 md:px-6">
          <FaqItem
            icon={MapPin}
            question="Onde fica a fábrica?"
            answer={
              <p>A fábrica fica em <strong>Jaraguá - GO</strong>. É de lá que sai cada peça, desde a escolha do tecido até o acabamento final.</p>
            }
          />

          <FaqItem
            icon={Store}
            question="Tem loja física?"
            answer={
              <>
                <p><strong>Sim!</strong> A loja fica em <strong>Jaraguá - GO</strong>. Se você quiser conhecer, será muito bem recebido e conhecerá todos os nossos produtos disponíveis.</p>
                <p>
                  É só entrar no nosso WhatsApp que mandaremos a localização exata!
                </p>
                <a
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-2 px-4 py-2 bg-[#25D366]/10 text-[#25D366] rounded-lg text-xs font-semibold hover:bg-[#25D366]/20 transition-colors"
                >
                  <MessageCircle className="h-4 w-4" />
                  Pedir localização no WhatsApp
                </a>
              </>
            }
          />

          <FaqItem
            icon={ShieldCheck}
            question="Posso confiar nesse site?"
            answer={
              <>
                <p><strong>Sim!</strong> Temos mais de <strong>5 anos de vendas online</strong> e mais de <strong>10 anos de loja física</strong>. Nossa reputação é construída com transparência e qualidade.</p>
                <p>
                  Caso queira ver depoimentos e parcerias, visite nosso Instagram:
                </p>
                <a
                  href={INSTAGRAM_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-2 px-4 py-2 bg-gradient-to-r from-[#833AB4] via-[#C13584] to-[#F77737] text-white rounded-lg text-xs font-semibold hover:opacity-90 transition-opacity"
                >
                  <Instagram className="h-4 w-4" />
                  Ver no Instagram
                </a>
              </>
            }
          />

          <FaqItem
            icon={Gem}
            question="Os produtos são de qualidade?"
            answer={
              <>
                <p><strong>Sim!</strong> Trabalhamos com os melhores tecidos do mercado — os mesmos que grandes marcas conhecidas utilizam.</p>
                <p className="text-xs text-neutral-400 italic">
                  * Não podemos citar nomes por causa de diretrizes comerciais.
                </p>
                <p>
                  Cada peça passa por um controle de qualidade rigoroso, desde a costura até o acabamento, garantindo durabilidade e conforto.
                </p>
              </>
            }
          />

          <FaqItem
            icon={Lock}
            question="O site é seguro?"
            answer={
              <>
                <p><strong>Sim!</strong> O site usa <strong>SSL 256-bit criptografado</strong> para dar a maior segurança aos nossos usuários.</p>
                <p>
                  Seus dados de pagamento são processados <strong>somente pelo Mercado Pago</strong>, que é conceituado no mercado por sua segurança.
                </p>
                <p className="text-[#1a1a1a] font-medium bg-neutral-50 rounded-lg p-3 border border-neutral-100">
                  O nosso site <strong>não tem acesso</strong> a cartões ou a qualquer outro dado de pagamento dos clientes.
                </p>
              </>
            }
          />

          <FaqItem
            icon={RotateCcw}
            question="Trocas e Devoluções"
            answer={
              <>
                <p>A <strong>Outlet Premium</strong> oferece troca grátis em até <strong>7 dias corridos</strong> após o recebimento do produto, conforme o Código de Defesa do Consumidor (Art. 49).</p>
                <p className="font-semibold text-[#1a1a1a] pt-1">Condições para troca ou devolução:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>O produto deve estar <strong>sem uso</strong>, com etiquetas originais e na embalagem</li>
                  <li>O prazo de 7 dias começa a contar a partir da <strong>data de recebimento</strong></li>
                  <li>Produtos com defeito de fabricação podem ser trocados em até <strong>30 dias</strong></li>
                  <li>Peças em promoção seguem as mesmas regras de troca</li>
                </ul>
                <p className="font-semibold text-[#1a1a1a] pt-1">Como solicitar:</p>
                <ol className="list-decimal pl-5 space-y-1">
                  <li>Entre em contato pelo nosso <strong>WhatsApp</strong></li>
                  <li>Informe o número do pedido e o motivo da troca/devolução</li>
                  <li>Enviaremos as instruções e o código de postagem (frete grátis)</li>
                  <li>Após recebermos o produto, a troca ou reembolso será processado em até <strong>5 dias úteis</strong></li>
                </ol>
                <p className="font-semibold text-[#1a1a1a] pt-1">Reembolso:</p>
                <p>O reembolso é feito pelo mesmo método de pagamento utilizado na compra. Para pagamentos via PIX, o valor é devolvido em até <strong>3 dias úteis</strong>. Para cartão de crédito, o estorno pode levar até <strong>2 faturas</strong>, dependendo da operadora.</p>
              </>
            }
          />
        </div>
      </section>

      {/* Trust Badges */}
      <section className="bg-[#F7F7F7] py-10 md:py-14">
        <div className="max-w-3xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm">
                <Lock className="h-5 w-5 text-[#C4A35A]" />
              </div>
              <p className="text-xs font-semibold text-[#1a1a1a]">SSL 256-bit</p>
              <p className="text-[10px] text-neutral-500">Criptografia ativa</p>
            </div>
            <div>
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm">
                <ShieldCheck className="h-5 w-5 text-[#C4A35A]" />
              </div>
              <p className="text-xs font-semibold text-[#1a1a1a]">Mercado Pago</p>
              <p className="text-[10px] text-neutral-500">Pagamento protegido</p>
            </div>
            <div>
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm">
                <Store className="h-5 w-5 text-[#C4A35A]" />
              </div>
              <p className="text-xs font-semibold text-[#1a1a1a]">+10 anos</p>
              <p className="text-[10px] text-neutral-500">Loja física</p>
            </div>
            <div>
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm">
                <Gem className="h-5 w-5 text-[#C4A35A]" />
              </div>
              <p className="text-xs font-semibold text-[#1a1a1a]">Qualidade</p>
              <p className="text-[10px] text-neutral-500">Tecidos premium</p>
            </div>
          </div>
        </div>
      </section>

      {/* ====== POLÍTICA DE PRIVACIDADE & TERMOS (Expandíveis) ====== */}
      <section className="max-w-3xl mx-auto px-4 py-12 md:py-16">
        <div className="border border-neutral-200 rounded-xl overflow-hidden">
          {/* Política de Privacidade */}
          <ExpandableSection id="privacidade" title="Política de Privacidade">
            <div className="text-sm text-neutral-600 leading-relaxed space-y-4">
              <p>A <strong>Outlet Premium</strong> respeita a sua privacidade e se compromete a proteger os dados pessoais dos seus clientes, em conformidade com a <strong>Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018)</strong>.</p>

              <h3 className="text-[#1a1a1a] font-semibold text-sm pt-2">Dados que coletamos:</h3>
              <ul className="list-disc pl-5 space-y-1.5">
                <li><strong>Dados de cadastro:</strong> nome, e-mail, telefone e endereço de entrega</li>
                <li><strong>Dados de navegação:</strong> páginas visitadas, produtos visualizados e tempo de permanência</li>
                <li><strong>Dados de compra:</strong> histórico de pedidos e preferências</li>
              </ul>

              <h3 className="text-[#1a1a1a] font-semibold text-sm pt-2">Como utilizamos seus dados:</h3>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Processar e entregar seus pedidos</li>
                <li>Enviar atualizações sobre status de entrega</li>
                <li>Oferecer promoções e novidades (com seu consentimento)</li>
                <li>Melhorar a experiência de navegação no site</li>
              </ul>

              <h3 className="text-[#1a1a1a] font-semibold text-sm pt-2">Segurança dos dados:</h3>
              <p>Utilizamos <strong>criptografia SSL 256-bit</strong> em todo o site. Os dados de pagamento são processados exclusivamente pelo <strong>Mercado Pago</strong> — não armazenamos dados de cartão de crédito ou qualquer informação financeira dos clientes.</p>

              <h3 className="text-[#1a1a1a] font-semibold text-sm pt-2">Cookies:</h3>
              <p>Utilizamos cookies para melhorar sua experiência de navegação, lembrar preferências e analisar o tráfego do site. Você pode desativar os cookies nas configurações do seu navegador a qualquer momento.</p>

              <h3 className="text-[#1a1a1a] font-semibold text-sm pt-2">Seus direitos:</h3>
              <p>Você pode solicitar a qualquer momento a <strong>consulta, correção ou exclusão</strong> dos seus dados pessoais entrando em contato pelo nosso WhatsApp ou e-mail.</p>
            </div>
          </ExpandableSection>

          {/* Termos de Uso */}
          <ExpandableSection id="termos" title="Termos de Uso">
            <div className="text-sm text-neutral-600 leading-relaxed space-y-4">
              <p>Ao acessar e utilizar o site da <strong>Outlet Premium</strong>, você concorda com os termos e condições descritos abaixo. Leia atentamente antes de realizar qualquer compra.</p>

              <h3 className="text-[#1a1a1a] font-semibold text-sm pt-2">1. Sobre o site</h3>
              <p>Este site é operado pela Outlet Premium, empresa do ramo de confecção e comércio de roupas, com sede em Jaraguá - GO. Todos os produtos são de fabricação própria e vendidos diretamente ao consumidor final.</p>

              <h3 className="text-[#1a1a1a] font-semibold text-sm pt-2">2. Preços e pagamentos</h3>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Todos os preços são exibidos em <strong>Reais (R$)</strong> e podem ser alterados sem aviso prévio</li>
                <li>Os pagamentos são processados pelo <strong>Mercado Pago</strong>, aceitos: cartão de crédito (até 12x), PIX e boleto</li>
                <li>O desconto de 5% no PIX é aplicado automaticamente</li>
              </ul>

              <h3 className="text-[#1a1a1a] font-semibold text-sm pt-2">3. Entrega</h3>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Enviamos para todo o Brasil via transportadoras parceiras</li>
                <li>O prazo de entrega varia de acordo com a região e é informado no momento da compra</li>
                <li>Pedidos acima de R$ 599 possuem <strong>frete grátis</strong></li>
              </ul>

              <h3 className="text-[#1a1a1a] font-semibold text-sm pt-2">4. Propriedade intelectual</h3>
              <p>Todo o conteúdo do site — incluindo textos, imagens, logotipos, vídeos e design — é de propriedade da Outlet Premium e protegido por leis de direitos autorais. É proibida a reprodução sem autorização prévia.</p>

              <h3 className="text-[#1a1a1a] font-semibold text-sm pt-2">5. Responsabilidades do usuário</h3>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Fornecer informações verdadeiras no cadastro e na compra</li>
                <li>Manter seus dados de acesso em sigilo</li>
                <li>Utilizar o site de forma lícita e respeitosa</li>
              </ul>

              <h3 className="text-[#1a1a1a] font-semibold text-sm pt-2">6. Alterações nos termos</h3>
              <p>A Outlet Premium reserva-se o direito de alterar estes termos a qualquer momento. As alterações entram em vigor imediatamente após a publicação no site.</p>

              <h3 className="text-[#1a1a1a] font-semibold text-sm pt-2">7. Contato</h3>
              <p>Em caso de dúvidas sobre estes termos, entre em contato pelo nosso WhatsApp ou e-mail.</p>
            </div>
          </ExpandableSection>
        </div>
      </section>

      {/* CTA final */}
      <section className="max-w-3xl mx-auto px-4 py-12 md:py-16 text-center">
        <div className="border-t border-neutral-200 pt-10">
        <p className="text-neutral-500 text-sm mb-6">Ainda tem alguma dúvida?</p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#25D366] text-white rounded-lg text-sm font-semibold hover:bg-[#20bd5a] transition-colors"
          >
            <MessageCircle className="h-4 w-4" />
            Fale conosco no WhatsApp
          </a>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 border border-neutral-300 text-neutral-600 rounded-lg text-sm font-medium hover:border-neutral-400 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar para a loja
          </Link>
        </div>
        </div>
      </section>
    </div>
  );
}
